/**
 * TrustRoomGate.jsx
 * GRANULLO_TRUST_ROOM_ROLE_EXPANSION_ZERO_COMPROMISE_V1
 *
 * Lightweight access gate for /trust-room/:role/* routes.
 * Token-based authentication per role.
 * Wraps children in StakeholderConvictionProvider.
 * Sets activeRole from URL params.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, ArrowRight } from 'lucide-react';
import { StakeholderConvictionProvider, useConvictionSpine } from '../../contexts/StakeholderConvictionStateSpine';

const API = process.env.REACT_APP_BACKEND_URL;

const TrustRoomContext = createContext(null);

export function useTrustRoom() {
  return useContext(TrustRoomContext);
}

function TrustRoomLoginGate({ children }) {
  const { role } = useParams();
  const navigate = useNavigate();
  const [authenticated, setAuthenticated] = useState(false);
  const [checking, setChecking] = useState(true);
  const [token, setToken] = useState('');
  const [error, setError] = useState('');
  const [roleConfig, setRoleConfig] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Check existing session
  useEffect(() => {
    async function checkSession() {
      try {
        const res = await fetch(`${API}/api/trust-room/session/${role}`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setRoleConfig(data.config);
          setAuthenticated(true);
        }
      } catch { /* no session */ }
      setChecking(false);
    }
    checkSession();
  }, [role]);

  const handleLogin = useCallback(async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/trust-room/validate-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token, role }),
      });
      if (res.ok) {
        const data = await res.json();
        setRoleConfig(data.config);
        setAuthenticated(true);
      } else {
        setError('Invalid access token');
      }
    } catch {
      setError('Connection error');
    }
    setSubmitting(false);
  }, [token, role]);

  const handleLogout = useCallback(async () => {
    await fetch(`${API}/api/trust-room/logout/${role}`, { method: 'POST', credentials: 'include' });
    setAuthenticated(false);
    setRoleConfig(null);
    navigate('/trust-room/' + role);
  }, [role, navigate]);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--shell-bg)' }}>
        <p className="text-[12px] font-mono" style={{ color: 'var(--shell-text-muted)' }}>Verifying access...</p>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: 'var(--shell-bg)' }}
        data-testid="trust-room-gate">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-[380px]">
          <div className="flex items-center gap-2 mb-6">
            <Lock size={14} style={{ color: 'var(--shell-text-muted)' }} />
            <p className="text-[11px] font-mono uppercase tracking-[0.14em]"
              style={{ color: 'var(--shell-text-muted)' }}>
              Trust Room — {role?.replace(/_/g, ' ')}
            </p>
          </div>

          <h1 className="text-[28px] leading-[1.1] tracking-[-0.02em] mb-2"
            style={{ fontFamily: '"Source Serif 4", Georgia, serif', fontWeight: 600, color: 'var(--shell-text)' }}>
            Granullo Trust Room
          </h1>
          <p className="text-[13px] leading-[1.5] mb-6" style={{ color: 'var(--shell-text-secondary)' }}>
            Enter your access token to view the shared conviction truth spine
            through the {role?.replace(/_/g, ' ')} lens.
          </p>

          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={token}
              onChange={e => setToken(e.target.value)}
              placeholder="Access token"
              className="w-full px-4 py-3 rounded text-[13px] font-mono mb-3"
              style={{
                background: 'var(--g-surface)',
                border: error ? '1px solid var(--shell-scarlet, #CD2200)' : '1px solid var(--g-border)',
                color: 'var(--shell-text)',
                outline: 'none',
              }}
              data-testid="trust-room-token-input"
              autoFocus
            />
            {error && (
              <p className="text-[11px] mb-3" style={{ color: 'var(--shell-scarlet, #CD2200)' }}
                data-testid="trust-room-error">{error}</p>
            )}
            <button type="submit" disabled={!token || submitting}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded text-[12px] font-mono uppercase tracking-[0.06em]"
              style={{
                background: token ? 'var(--shell-text)' : 'var(--g-border)',
                color: token ? 'var(--shell-bg)' : 'var(--shell-text-muted)',
                border: 'none',
                cursor: token ? 'pointer' : 'not-allowed',
                opacity: submitting ? 0.6 : 1,
              }}
              data-testid="trust-room-submit">
              {submitting ? 'Verifying...' : 'Enter Trust Room'}
              <ArrowRight size={12} />
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <TrustRoomContext.Provider value={{ role, roleConfig, handleLogout }}>
      {children}
    </TrustRoomContext.Provider>
  );
}

/**
 * RoleSetter — Sets activeRole on the conviction spine from URL params.
 * Must be inside StakeholderConvictionProvider.
 */
function RoleSetter({ children }) {
  const { role } = useParams();
  const { setActiveRole, activeRole } = useConvictionSpine();

  useEffect(() => {
    if (role && role !== activeRole) {
      setActiveRole(role);
    }
  }, [role, activeRole, setActiveRole]);

  return children;
}

/**
 * TrustRoomGate — Full gate wrapper for /trust-room/:role/* routes.
 * Handles auth → conviction provider → role setting.
 */
export default function TrustRoomGate({ children }) {
  return (
    <TrustRoomLoginGate>
      <StakeholderConvictionProvider>
        <RoleSetter>
          {children}
        </RoleSetter>
      </StakeholderConvictionProvider>
    </TrustRoomLoginGate>
  );
}
